import Compressor from 'compressorjs'

/** Why compression fell back to the original file (for diagnostics — see `onFallback`). */
export type CompressFallbackReason = 'timeout' | 'error'

export interface CompressFileOptions {
  file: File
  maxWidth?: number
  maxHeight?: number
  quality?: number
  width?: number
  /**
   * Called when compression couldn't complete and we fell back to the original
   * file (compressorjs errored, or it never called back within the timeout).
   * Surfaced so callers can record it — a silent fallback on iOS is a prime
   * suspect for the heavy/failing iPad uploads in #21104. Never called on a
   * normal successful compress.
   */
  onFallback?: (reason: CompressFallbackReason, error?: Error) => void
}

/**
 * compressorjs settles its callback via an `<img>`/canvas decode. On iOS/iPadOS
 * WebKit that decode can silently fail to fire either `success` or `error` for
 * some photos, leaving the promise pending forever — which strands the upload
 * (`isUploading` never clears) and permanently disables the "Done" button (#21104).
 *
 * Compression is an optimization, not a requirement, so if it doesn't finish in
 * time (or errors) we fall back to uploading the original file rather than
 * hanging. Trade-off: the original may be a large (e.g. 12MP+) photo, so the
 * fallback can mean a heavier S3 upload — we accept that over a frozen "Done".
 *
 * Kept well under the 60s S3 PUT + 90s overall guard (EditItemFilesDialogNext)
 * so a stuck decode resolves quickly instead of making "Done" feel dead for the
 * ~5s users actually wait before giving up.
 */
const COMPRESS_TIMEOUT_MS = 8000

// Compress images, return compressed image or skipped file
export default async function compressFile({
  file,
  maxHeight = null,
  maxWidth = null,
  onFallback
}: CompressFileOptions) {
  if (!file.type || !file.type.startsWith('image') || file.type === 'image/gif') {
    return file
  }

  const newFile = await new Promise<File>((resolve) => {
    let settled = false
    const settle = (result: File) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      resolve(result)
    }

    // Fall back to the original file if compressorjs never calls back.
    const timeoutId = setTimeout(() => {
      onFallback?.('timeout')
      settle(file)
    }, COMPRESS_TIMEOUT_MS)

    new Compressor(file, {
      // https://www.npmjs.com/package/compressorjs#quality
      maxHeight: maxHeight ?? 1200,
      maxWidth: maxWidth ?? 1200,
      success(result) {
        settle(result as File)
      },
      // Compression failed (e.g. canvas too large on iOS) — upload the original.
      error(err) {
        onFallback?.('error', err)
        settle(file)
      }
    })
  })

  return newFile
}
